import React, { useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { startCase } from 'lodash';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import type { SubsetFrom } from 'Util/TypeHelpers';

import SecuredPage from 'Views/Components/Security/SecuredPage';
import Navbar from 'Views/Components/Navbar/Navbar';
import Chip from 'Views/Components/Chip/Chip';
import Skeleton, { Colors } from 'Views/Components/Skeleton/Skeleton';
import { Button, Display } from 'Views/Components/Button/Button';

import { useOnMount } from 'Hooks/useMounted';
import { getAllStaffUserType } from 'Queries/GraphQlQueries/User/UserEntityQueries';
import {
	AdminEntity,
	CurriculumSupportEntity, IAdminEntityAttributes, ICurriculumSupportEntityAttributes,
	IInstitutionAdminEntityAttributes, InstitutionAdminEntity,
	IStudentAdvisorEntityAttributes, StudentAdvisorEntity,
} from 'Models/Entities';
import { getStatus, UserStatusFields } from 'Util/UserUtils';
import { userDiscriminatorRoleMap, UserEntities, userModelDisplayNameMap } from 'Models/Enums';
import StudyPlanCollection from 'Views/Components/StudyPlanCollection/StudyPlanCollection';
import alertToast from 'Util/ToastifyUtils';
import usePermission from 'Hooks/usePermission';
import { hasAnyRole } from 'Util/SecurityUtils';
import useStore from 'Hooks/useStore';
import ActivateDeactivateButton from 'Views/Pages/Frontend/UserManagement/ActivateDeactivateButton';

const StaffDiscriminators = [
	'AdminEntity', 'CurriculumSupportEntity',
	'StudentAdvisorEntity', 'InstitutionAdminEntity',
] as const;

// ensure staff types are subset of available user entities
type SelectableDiscriminators = SubsetFrom<
	UserEntities,
	typeof StaffDiscriminators[number]
>;

const getModelTypeFromDiscriminator = (discriminator: SelectableDiscriminators) => {
	switch (discriminator) {
		case 'AdminEntity':
			return AdminEntity;
		case 'InstitutionAdminEntity':
			return InstitutionAdminEntity;
		case 'CurriculumSupportEntity':
			return CurriculumSupportEntity;
		case 'StudentAdvisorEntity':
			return StudentAdvisorEntity;
		default:
			throw new Error(`Unknown discriminator '${discriminator}'`);
	}
};

const getUserTypeForGraphQLResponse = (queryData: any) => {
	switch (queryData.__typename) {
		case 'AdminEntityType':
			return queryData as (IAdminEntityAttributes & UserStatusFields);
		case 'StudentAdvisorEntityType':
			return queryData as (IStudentAdvisorEntityAttributes & UserStatusFields);
		case 'CurriculumSupportEntityType':
			return queryData as (ICurriculumSupportEntityAttributes & UserStatusFields);
		case 'InstitutionAdminEntityType':
			return queryData as (IInstitutionAdminEntityAttributes & UserStatusFields);
		default:
			throw new Error(`User query type name ${queryData.__typename} not recognised`);
	}
};

export type StaffUserEntityAttributes = IAdminEntityAttributes
	| ICurriculumSupportEntityAttributes
	| IStudentAdvisorEntityAttributes
	| IInstitutionAdminEntityAttributes;

export const StaffDetailPage = () => {
	const { userId, discriminator } = useParams<{ discriminator?: SelectableDiscriminators, userId: string }>();
	const history = useHistory();
	const store = useStore();

	const [user, setUser] = useState<StaffUserEntityAttributes & UserStatusFields>();

	const isDiscriminatorSupported = discriminator && StaffDiscriminators.includes(discriminator);

	const hasPermissionToDeletePlans = usePermission([
		userDiscriminatorRoleMap.AdminEntity,
		userDiscriminatorRoleMap.InstitutionAdminEntity,
	]);

	useOnMount(
		async (isMounted, abortSignals) => {
			if (!userId || !discriminator) {
				return;
			}

			const userResponse = await getAllStaffUserType(
				abortSignals,
				[],
				[],
				[],
				0,
				1,
				[userId],
				getModelTypeFromDiscriminator(discriminator),
				// Admin Entity does not have name properties
				// only include names for the other user types
				discriminator !== 'AdminEntity',
			);

			Object.keys(userResponse.data)
				.forEach(property => {
					const queryData = (userResponse.data as any)[property];

					if (Array.isArray(queryData) && queryData.length > 0) {
						const userAttributes = getUserTypeForGraphQLResponse(queryData[0]);
						setUser(userAttributes);
					}
				});
		},
		[userId],
	);

	if (!isDiscriminatorSupported) {
		alertToast(
			`Specified user type '${discriminator}' is not supported`,
			'error',
		);

		return <Redirect to="/404" />;
	}

	return (
		<SecuredPage
			groups={[
				'Admin',
				'InstitutionAdmin',
				'CurriculumSupport',
				'StudentAdvisor',
			]}
		>
			<div className="body-content">
				<Navbar />
				<main className="user-detail-page">
					<div className="action-buttons">
						<Button onClick={() => history.replace('/users?tab=staff')}>
							Go back
						</Button>
						{
							hasAnyRole(store, ['Admin', 'InstitutionAdmin']) && user
								? (
									<span>
										<Button
											className="edit"
											onClick={() => history.push(`/users/staff/edit/${discriminator}/${userId}`)}
											display={Display.Outline}
										>
											Edit
										</Button>
										<ActivateDeactivateButton
											user={user}
											onAfterSave={u => setUser(u)}
										/>
									</span>
								) : null
						}
					</div>
					<StaffDetails
						extendedUser={user}
						userGroup={discriminator}
					/>
					<StudyPlanCollection
						userId={userId}
						userDiscriminator={discriminator}
						studentView={false}
						canDelete={hasPermissionToDeletePlans}
						showActiveToggle={false}
					/>
				</main>
			</div>
		</SecuredPage>
	);
};

interface IStaffDetailProps {
	extendedUser?: StaffUserEntityAttributes & UserStatusFields ;
	userGroup?: SelectableDiscriminators;
}

export const StaffDetails: React.FunctionComponent<IStaffDetailProps> = ({
	extendedUser, userGroup,
}) => {
	const [animationRef] = useAutoAnimate();

	let content = null;
	// early return with loading UI
	if (!extendedUser || !userGroup) {
		content = <Skeleton backgroundColor={Colors.WHITE} lines={2} />;
	} else {
		const lowerCaseStatus = getStatus(
			extendedUser.lockoutEnd,
			extendedUser.emailConfirmed,
		).toLowerCase();
		const statusLabel = startCase(lowerCaseStatus);

		content = (
			<>
				<h3>
					<span className="user-names">
						{
							('firstName' in extendedUser && 'lastName' in extendedUser)
								// eslint-disable-next-line max-len
								? `${extendedUser.firstName || '(No first name)'} ${extendedUser.lastName || '(No last name)'}`
								: '(No first or last name)'
						}
					</span>
				</h3>
				<div className="labels">
					<span className="icon icon-left icon-mail">
						{extendedUser.email}
					</span>
					<span className="icon icon-left icon-business-center">
						{userModelDisplayNameMap[userGroup]}
					</span>
					<Chip
						chipLabel={statusLabel}
						className={`user-status ${lowerCaseStatus}`}
					/>
				</div>
			</>
		);
	}

	return (
		<div className="user-details staff" data-testid="staff-details">
			<div key="staff-details-animation-container" ref={animationRef}>
				{content}
			</div>
		</div>
	);
};
